<script lang="ts">
  import Form from "../../components/Form.svelte";
  import RadioGroup from "../../components/RadioGroup.svelte";
  import SharepointFileInput from "../../components/SharepointFileInput.svelte";
  import Caret from "~icons/ph/caret-down";
  import CalendarCheck from "~icons/ph/calendar-check";
  import { domId } from "../../lib/dom";

  let {
    action,
    errors,
    clientFirstName,
    grantOfProbateExampleUrl,
    createUploadSessionUrl,
    uploadAttachmentExtensions,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    clientFirstName: string;
    grantOfProbateExampleUrl: string;
    createUploadSessionUrl: string;
    uploadAttachmentExtensions: string[];
  } = $props();

  let details = $state(null);
  let hasDocument = $state("yes");
  let wantsHelp = $state("no");

  function hideDetails() {
    if (details) details.open = false;
  }

  const closeDialogId = domId("close-dialog");
</script>

<Form method="patch" {action} class="ui-rhythm">
  <h2 class="ui-title">Upload grant of probate</h2>

  <p>
    This document proves the validity of {clientFirstName}’s&nbsp;will.
  </p>

  <details class="ui-details">
    <summary>
      <Caret />What is a grant of probate and how do I get one?
    </summary>

    <p>
      A grant of probate is an official legal document issued by a Probate Registry that authorises executors named
      in a will to manage, sell, and distribute a deceased person’s estate. It proves the will’s validity and gives
      legal authority to access bank accounts, pay debts, and transfer assets.
      If you are the client’s executor, you can apply for one at the
      <a href="https://www.gov.uk/applying-for-probate" target="_blank" rel="noreferrer" class="link text-blue-600">
        Probate Registry
      </a>. Please note that if the estate’s value exceeds £5,000, there is a flat fee of £300
      payable for obtaining the relevant grant. For estates valued below £5,000, no application fee applies.
    </p>
    <p>Example grant of probate:</p>
    <img src={grantOfProbateExampleUrl} alt="Example of grant of probate document" class="rounded-lg border mt-2" />
  </details>

  <fieldset class="ui-fieldset">
    <legend>Do you have the grant of probate?</legend>

    <RadioGroup
      name="has_document"
      options={[
        ["Yes", "yes"],
        ["Not yet, but I’ve started the process", "not_yet"],
        ["No, I haven’t started yet", "no"],
      ]}
      bind:group={hasDocument}
      required />
  </fieldset>

  <div class="ui-content-options mt-6">
    <fieldset class="ui-fieldset" class:invisible={hasDocument !== "yes"}>
      <legend>Upload grant of probate</legend>

      <div>
        <p class="ui-alert ui-alert-info pb-4 -mb-2.5">Please provide clear and legible photo(s) of the grant of probate. Add all pages before sending.</p>
        <SharepointFileInput
          name="grant_of_probate"
          accept={uploadAttachmentExtensions}
          {createUploadSessionUrl}
          multiple
          required={hasDocument === "yes"} />
      </div>

      {#if errors?.grant_of_probate}<div class="ui-error">{errors.grant_of_probate}</div>{/if}
    </fieldset>

    <div class:invisible={hasDocument === "yes"}>
      <fieldset class="ui-fieldset">
        <legend>If you’d like help with these documents, a law firm we’re partnering with can reach out:</legend>

        <div class="ui-option-container">
          <label class="flex flex-row gap-2 items-start">
            <input
              type="radio"
              class="radio"
              name="wants_help"
              value="yes"
              bind:group={wantsHelp}
              onchange={hideDetails}
              required
            >
            Yes, please reach out to me
          </label>

          <details class="ui-details mt-4" bind:this={details}>
            <summary><Caret /> How does this work?</summary>
            <ul class="ui-list pl-6">
              <li>We’ll refer you to a specialist probate firm, Private Client Solicitors, who can help with estate administration, such as grant of probate, or letters of administration</li>
              <li>They offer an initial consultation free of charge, where they can discuss any subsequent fees directly with you</li>
              <li>We don’t receive any fee or commission for this referral, and you’re free to choose any adviser you wish</li>
            </ul>
          </details>
        </div>

        <div class="ui-option-container">
          <label class="flex flex-row gap-2 items-start">
            <input
              type="radio"
              name="wants_help"
              value="no"
              class="radio"
              bind:group={wantsHelp}
              onchange={hideDetails}
              required
            >
            No, I can do it myself
          </label>
        </div>
      </fieldset>

      <div class="ui-alert ui-alert-info" class:invisible={wantsHelp !== "no"}>
        <CalendarCheck />
        <p>
          Take all the time you need, and we’ll check back in <b>{hasDocument === "not_yet" ? "two weeks" : "a month"}</b>.
          Until then, feel free to use our resources above to find this document. When you find it, you can upload it here.
        </p>
      </div>
    </div>
  </div>

  {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}

  <div class="floating-footer">
    <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    <button class="btn btn-primary" type="submit" data-viewer-mode>{hasDocument === "yes" ? "Send" : "Finish"}</button>
  </div>
</Form>

<form method="dialog" id={closeDialogId}></form>
